import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { PageLayout } from '../layout/PageLayout';

const ALLOWLIST = ['quasaralexander@gmail.com', 'qanvass@gmail.com'];

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    checkAuth();

    // Listen for auth state changes
    const { data: authListener } = supabase?.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_OUT') {
          setAuthenticated(false);
        } else if (session) {
          verifyAccess(session.user.email);
        }
      }
    ) ?? { data: null };

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  async function checkAuth() {
    if (!supabase) {
      setLoading(false);
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      setAuthenticated(false);
      setLoading(false);
      return;
    }

    verifyAccess(session.user.email);
  }

  function verifyAccess(email: string | undefined) {
    if (email && ALLOWLIST.includes(email)) {
      setAuthenticated(true);
      setAccessDenied(false);
    } else {
      setAuthenticated(false);
      setAccessDenied(true);
    }
    setLoading(false);
  }

  if (loading) {
    return <div className="flex h-screen items-center justify-center">Verifying access...</div>;
  }

  if (accessDenied) {
    return (
      <PageLayout title="Access Denied" subtitle="You do not have permission to view this page.">
        <div className="text-center p-10">
          <p className="mb-4">Your email is not on the admin allowlist.</p>
          <button 
            onClick={() => supabase?.auth.signOut()}
            className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-black"
          >
            Sign Out
          </button>
        </div>
      </PageLayout>
    );
  }

  if (!authenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
}
